import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ClockifyTimeEntriesState } from "./ClockifyTimeEntriesState";
import { fetchClockifyTimeEntriesAsync } from "./clockifyTimeEntriesApi";
import { ClockifyTimeEntriesSortConfig } from "./ClockifyTimeEntriesSortConfig";
import { ClockifyTimeEntry } from "./ClockifyTimeEntry";

const initialState: ClockifyTimeEntriesState = {
  timeEntries: [],
  initializationStatus: "not-initialized",
  status: "idle",
  pagination: {
    currentPage: 1,
    pageSize: 100,
    totalPages: 0,
    totalItems: 0,
  },
  sortConfig: {
    field: "dateRange",
    direction: "desc",
  },
  sortApplied: true,
};

export const calculateBudgetaryPerformance = (
  entry: ClockifyTimeEntry
): number => {
  const totalTimeSpent = entry.totalSpentTimeOnTaskInHours;

  const taskBudget = entry.taskTimeBudget;

  const percentComplete = entry.percentageComplete;

  if (taskBudget <= 0 || percentComplete <= 0) {
    return 0;
  }

  return totalTimeSpent / (taskBudget * (percentComplete / 100));
};

export const calculateBudgetaryPerformanceWithoutPercentage = (
  entry: ClockifyTimeEntry
): number => {
  const totalTimeSpent = entry.totalSpentTimeOnTaskInHours;

  const taskBudget = entry.taskTimeBudget;

  if (taskBudget <= 0) {
    return 0;
  }

  return totalTimeSpent / taskBudget;
};

const updateTotalPages = (state: ClockifyTimeEntriesState) => {
  state.pagination.totalPages = Math.ceil(
    state.pagination.totalItems / state.pagination.pageSize
  );
};

export const clockifyTimeEntriesSlice = createSlice({
  name: "clockifyTimeEntries",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      const maxPage = Math.max(1, state.pagination.totalPages);
      state.pagination.currentPage = Math.min(
        maxPage,
        Math.max(1, action.payload)
      );
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
      updateTotalPages(state);
    },
    setSortConfig: (
      state,
      action: PayloadAction<ClockifyTimeEntriesSortConfig>
    ) => {
      state.sortConfig = action.payload;
      state.sortApplied = true;
    },
    sortTimeEntries: (state) => {
      const { sortConfig } = state;
      const { timeEntries } = state;
      const sortedEntries = [...timeEntries].sort((a, b) => {
        if (sortConfig.field === "dateRange") {
          const dateA = new Date(a.dateStart);
          const dateB = new Date(b.dateStart);
          return sortConfig.direction === "asc"
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        } else if (sortConfig.field === "budgetaryPerformance") {
          const hasPercentageA =
            a.percentageComplete !== undefined &&
            a.percentageComplete !== null &&
            a.percentageComplete > 0;
          const hasPercentageB =
            b.percentageComplete !== undefined &&
            b.percentageComplete !== null &&
            b.percentageComplete > 0;

          const budgetaryPerformanceA = hasPercentageA
            ? calculateBudgetaryPerformance(a)
            : calculateBudgetaryPerformanceWithoutPercentage(a);
          const budgetaryPerformanceB = hasPercentageB
            ? calculateBudgetaryPerformance(b)
            : calculateBudgetaryPerformanceWithoutPercentage(b);
          return sortConfig.direction === "asc"
            ? budgetaryPerformanceA - budgetaryPerformanceB
            : budgetaryPerformanceB - budgetaryPerformanceA;
        } else {
          const aValue = a[sortConfig.field] as string | number;
          const bValue = b[sortConfig.field] as string | number;

          if (typeof aValue === "string" && typeof bValue === "string") {
            return sortConfig.direction === "asc"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          } else {
            const numA = typeof aValue === "number" ? aValue : 0;
            const numB = typeof bValue === "number" ? bValue : 0;
            return sortConfig.direction === "asc" ? numA - numB : numB - numA;
          }
        }
      });
      state.timeEntries = sortedEntries;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClockifyTimeEntriesAsync.pending, (state) => {
        state.status = "loading";
        if (state.initializationStatus === "not-initialized") {
          state.initializationStatus = "initializing";
        }
      })
      .addCase(fetchClockifyTimeEntriesAsync.fulfilled, (state, action) => {
        state.status = "idle";
        state.timeEntries = action.payload;
        state.pagination.totalItems = state.timeEntries.length;
        updateTotalPages(state);
        state.initializationStatus = "initialized";

        if (state.sortApplied) {
          clockifyTimeEntriesSlice.caseReducers.sortTimeEntries(state);
        }
      })
      .addCase(fetchClockifyTimeEntriesAsync.rejected, (state, action) => {
        state.status = "failed";
        if (state.initializationStatus === "initializing") {
          state.initializationStatus = "not-initialized";
        }
        console.error("Fetch rejected:", action.payload);
      });
  },
});

export const { setPage, setPageSize, setSortConfig, sortTimeEntries } =
  clockifyTimeEntriesSlice.actions;

export const selectClockifyTimeEntries = (state: RootState) =>
  state.clockifyTimeEntries.timeEntries;
export const selectClockifyStatus = (state: RootState) =>
  state.clockifyTimeEntries.status;
export const selectClockifyLoading = (state: RootState) =>
  state.clockifyTimeEntries.status === "loading";
export const selectClockifyPagination = (state: RootState) =>
  state.clockifyTimeEntries.pagination;
export const selectClockifySortConfig = (state: RootState) =>
  state.clockifyTimeEntries.sortConfig;
export const selectClockifySortApplied = (state: RootState) =>
  state.clockifyTimeEntries.sortApplied;
export const selectClockifyTotalItems = (state: RootState) =>
  state.clockifyTimeEntries.pagination.totalItems;
export const selectClockifyInitializationStatus = (state: RootState) =>
  state.clockifyTimeEntries.initializationStatus;

export default clockifyTimeEntriesSlice.reducer;
